import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, WhatsappLogo, CircleNotch } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER;
const PHONE_DISPLAY = process.env.REACT_APP_PHONE_DISPLAY;
const WHATSAPP_URL = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Olá! Gostaria de solicitar um orçamento de TI para minha empresa.")}`;

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  segment: "",
  employees: "",
  message: "",
};

const inputClass =
  "rounded-sm border-black/15 bg-white focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold h-12 text-sm";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.segment) {
      toast.error("Selecione o segmento da sua empresa.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, employees: form.employees || null, message: form.message || null });
      toast.success("Orçamento solicitado!", {
        description: "Nossa equipe entrará em contato em breve. Obrigado pela confiança.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("Não foi possível enviar.", {
        description: "Tente novamente ou fale conosco pelo WhatsApp.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" data-testid="contact-section" className="py-24 lg:py-32 bg-white border-t border-black/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p data-testid="contact-eyebrow" className="text-xs font-semibold tracking-[0.3em] uppercase text-gold-dark mb-6">
            Contato
          </p>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02]">
            Vamos organizar
            <span className="block text-gold-dark">a sua TI?</span>
          </h2>
          <p data-testid="contact-text" className="mt-8 text-base md:text-lg text-neutral-600 leading-relaxed max-w-md">
            Conte sobre a sua operação e receba uma proposta sob medida — sem compromisso,
            com a clareza que a sua empresa merece.
          </p>
          <div className="mt-10 space-y-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-phone-link"
              className="group flex items-center gap-4"
            >
              <span className="font-display font-black tracking-tight text-3xl text-ink group-hover:text-gold-dark transition-colors duration-300">
                {PHONE_DISPLAY}
              </span>
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="inline-flex items-center gap-3 border border-ink/15 px-6 py-4 rounded-sm font-semibold text-ink hover:border-gold hover:text-gold-dark transition-colors duration-300"
            >
              <WhatsappLogo size={22} weight="duotone" className="text-[#25D366]" />
              Prefere conversar? Chame no WhatsApp
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 bg-[#FAFAFA] border border-black/5 rounded-sm p-8 lg:p-12"
          data-testid="quote-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="quote-name" className="text-xs font-semibold tracking-widest uppercase text-neutral-500">Nome*</Label>
              <Input id="quote-name" data-testid="quote-name-input" required minLength={2} placeholder="Seu nome" value={form.name} onChange={set("name")} className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote-company" className="text-xs font-semibold tracking-widest uppercase text-neutral-500">Empresa*</Label>
              <Input id="quote-company" data-testid="quote-company-input" required minLength={2} placeholder="Nome da empresa" value={form.company} onChange={set("company")} className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote-email" className="text-xs font-semibold tracking-widest uppercase text-neutral-500">E-mail*</Label>
              <Input id="quote-email" data-testid="quote-email-input" required type="email" placeholder="voce@empresa.com.br" value={form.email} onChange={set("email")} className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote-phone" className="text-xs font-semibold tracking-widest uppercase text-neutral-500">Telefone*</Label>
              <Input id="quote-phone" data-testid="quote-phone-input" required minLength={8} placeholder="(11) 99999-0000" value={form.phone} onChange={set("phone")} className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-widest uppercase text-neutral-500">Segmento*</Label>
              <Select value={form.segment} onValueChange={(v) => setForm((f) => ({ ...f, segment: v }))}>
                <SelectTrigger data-testid="quote-segment-select" className={inputClass}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="segment-option-clinica" value="clinica">Clínica</SelectItem>
                  <SelectItem data-testid="segment-option-restaurante" value="restaurante">Restaurante</SelectItem>
                  <SelectItem data-testid="segment-option-escritorio" value="escritorio">Escritório</SelectItem>
                  <SelectItem data-testid="segment-option-outro" value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-widest uppercase text-neutral-500">Nº de funcionários</Label>
              <Select value={form.employees} onValueChange={(v) => setForm((f) => ({ ...f, employees: v }))}>
                <SelectTrigger data-testid="quote-employees-select" className={inputClass}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="employees-option-5-10" value="5-10">5 a 10</SelectItem>
                  <SelectItem data-testid="employees-option-11-30" value="11-30">11 a 30</SelectItem>
                  <SelectItem data-testid="employees-option-31-50" value="31-50">31 a 50</SelectItem>
                  <SelectItem data-testid="employees-option-50plus" value="50+">Mais de 50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2 mt-6">
            <Label htmlFor="quote-message" className="text-xs font-semibold tracking-widest uppercase text-neutral-500">Mensagem</Label>
            <Textarea
              id="quote-message"
              data-testid="quote-message-input"
              placeholder="Conte um pouco sobre a sua operação e o que precisa..."
              value={form.message}
              onChange={set("message")}
              rows={4}
              className="rounded-sm border-black/15 bg-white focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold text-sm resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="quote-submit-button"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-ink text-[#FAFAFA] font-semibold px-8 py-4 rounded-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <CircleNotch size={20} className="animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <PaperPlaneTilt size={20} weight="duotone" />
                Solicitar orçamento
              </>
            )}
          </button>
          <p className="mt-4 text-xs text-neutral-400 text-center" data-testid="quote-form-disclaimer">
            Resposta em até 1 dia útil. Seus dados estão seguros conosco.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
